import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import PageSEO from "@/components/PageSEO";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import GuideArticleCard, { type GuideArticle } from "@/components/GuideArticleCard";
import BlogArticlesGrid from "@/components/BlogArticlesGrid";
import SiteBanner from "@/components/SiteBanner";
import { useAITranslate } from "@/hooks/useAITranslate";
import i18n from "@/i18n";

// Render answer text, turning any http(s) URL into a clickable link
const renderAnswer = (text: string) => {
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        className="text-primary underline underline-offset-4 hover:text-primary/80"
        rel="noopener"
      >
        {part.replace(/\/$/, "")}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

const Guide = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const lang = (i18nInstance.resolvedLanguage || i18nInstance.language || "fr").slice(0, 2);
  const [articles, setArticles] = useState<GuideArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [tLoading, tExcursionsHeading, tFaqHeading, tHome, tBadge, tH1] = useAITranslate([
    "Loading…",
    "Excursions & must-see places in Morocco",
    "Morocco travel tips 2026 — Frequently asked questions",
    "Home",
    "Morocco travel guide 2026",
    "Morocco Travel Guide: tips, cities and transfers",
  ], "en");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    (supabase as any)
      .from("guide_articles")
      .select("*")
      .order("slot", { ascending: true })
      .then(({ data }: { data: GuideArticle[] | null }) => {
        setArticles(data || []);
        setLoading(false);
      });
  }, []);

  // Always read FAQ from the canonical FR resources so AI translation works
  // even when the target language file (e.g. ar.json) is missing sections.
  const frSections =
    ((i18n.getResource("fr", "translation", "guideFaq.sections") as
      | { heading: string; items: { q: string; a: string }[] }[]
      | undefined) || []);
  const localSections =
    (t("guideFaq.sections", { returnObjects: true }) as
      | { heading: string; items: { q: string; a: string }[] }[]
      | undefined) || [];
  // Prefer the locale resource when complete, otherwise fall back to FR + AI.
  const baseSections =
    localSections.length >= frSections.length ? localSections : frSections;

  // Flatten every string from the FAQ for a single AI batch when lang !== fr.
  const flatTexts: string[] = [];
  baseSections.forEach((s) => {
    flatTexts.push(s.heading);
    s.items.forEach((it) => {
      flatTexts.push(it.q);
      flatTexts.push(it.a);
    });
  });
  const translatedFlat = useAITranslate(flatTexts);
  const useTranslated = lang !== "fr" && baseSections === frSections;
  let cursor = 0;
  const take = (src: string) => {
    const v = useTranslated ? translatedFlat[cursor] ?? src : src;
    cursor += 1;
    return v;
  };
  const sections = baseSections.map((s) => ({
    heading: take(s.heading),
    items: s.items.map((it) => ({ q: take(it.q), a: take(it.a) })),
  }));
  const allQuestions = sections.flatMap((s) => s.items);

  return (
    <main className="min-h-screen bg-white">
      <PageSEO
        title="Morocco Guide 2026 — Travel, transfers & excursions"
        description="Morocco Guide 2026: travel tips, private transfers, excursions and must-see places — Marrakech, Essaouira, Atlas, desert and Atlantic coast."
        path="/guide"
      />
      <Helmet>
        <script type="application/ld+json" id="guide-list-faq-jsonld">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: allQuestions.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Bannière éditable (AdminUp › Bannières › guide_hero) */}
      <div className="pt-20">
        <SiteBanner slot="guide_hero" />
      </div>

      <div className="pt-10 pb-20">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <a href="/" className="hover:text-primary">{tHome}</a>
            <span>›</span>
            <span className="text-foreground font-medium">Guide</span>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 border border-accent/30 px-4 py-1.5 text-xs md:text-sm font-semibold text-primary mb-3">
            <BookOpen className="h-4 w-4" />
            <span>{tBadge}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8">
            {tH1}
          </h1>


          {/* Blog & conseils de voyage — affiché en premier */}
          <div className="mt-8">
            <BlogArticlesGrid />
          </div>

          {/* Excursions & lieux incontournables — affichés ensuite */}
          {loading ? (
            <div className="text-center py-12 text-[#52525b]">{tLoading}</div>
          ) : articles.length > 0 ? (
            <section className="mb-16 mt-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-8">
                {tExcursionsHeading}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((a) => (
                  <GuideArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          ) : null}

          {/* FAQ Section */}
          <section aria-labelledby="guide-faq-heading">
            <div className="flex items-center gap-2 mb-6 mt-16">
              <HelpCircle className="h-5 w-5 text-accent" />
              <h2 id="guide-faq-heading" className="font-heading text-2xl md:text-3xl font-bold text-primary">
                {tFaqHeading}
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {sections.map((section, sIdx) => {
                const sVal = `gfq-section-${sIdx}`;
                return (
                  <AccordionItem
                    key={sVal}
                    value={sVal}
                    className="bg-secondary border border-border rounded-xl px-4 md:px-5 shadow-[0_2px_8px_rgba(60,40,20,0.04)]"
                  >
                    <AccordionTrigger className="font-heading text-left text-[#2b2b2b] hover:no-underline text-base md:text-lg font-semibold py-4">
                      <span className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                        {section.heading}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Accordion type="single" collapsible className="space-y-3 pt-2 pb-1">
                        {section.items.map((item, iIdx) => {
                          const val = `gfq-${sIdx}-${iIdx}`;
                          return (
                            <AccordionItem
                              key={val}
                              value={val}
                              className="bg-white border border-border rounded-lg px-4"
                            >
                              <AccordionTrigger className="font-heading text-left text-[#2b2b2b] hover:no-underline text-sm md:text-base py-3">
                                {item.q}
                              </AccordionTrigger>
                              <AccordionContent className="font-body text-[#5a5a5a] leading-relaxed text-sm md:text-base">
                                {renderAnswer(item.a)}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </section>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </main>
  );
};

export default Guide;
